import Decimal from "decimal.js"
import { SharingMinistryType } from "../../types/supabase"
import { getCurrentLimits, getSmiPlanLimits } from "../../lib/rules/limits"
import { Ministry, selectMinistry, selectSharingMinistry } from "../reduxToolkit/selectors/sharingMinistry"
import { useAppSelector } from "./reduxToolkit"

/**
 * The bare sharing ministry enum. Use this when you only need the ministry
 * identity (terminology, badges, "is this a ministry submission" checks). When
 * you also need the ministry's tier data, prefer `useMinistry`, which returns
 * a discriminated union so the tier fields travel with the ministry.
 */
export function useSharingMinistry(): SharingMinistryType {
  return useAppSelector(selectSharingMinistry)
}

/**
 * The member's sharing ministry and its tier data as a discriminated union.
 * The type system stops you from reading `iuaAmount`/`plan` for the wrong
 * ministry, so tier data can't get out of sync with the ministry identity.
 */
export function useMinistry(): Ministry {
  return useAppSelector(selectMinistry)
}

/**
 * The member's ministry display name and the qualifying threshold their
 * expenses must reach before an incident can be submitted for sharing:
 * CHM's annual qualifying amount, Sedera's chosen per-membership IUA, or
 * SMI's plan-level per-need IUA. Members with no ministry fall back to the
 * CHM display values (matching the previous inline behavior).
 */
export function useMinistryQualification(): { ministryName: string, qualifyingAmount: string | Decimal } {
  const ministry = useMinistry()
  const limits = getCurrentLimits()

  switch (ministry.type) {
    case 'Sedera':
      return {
        ministryName: 'Sedera',
        qualifyingAmount: ministry.iuaAmount != null ? String(ministry.iuaAmount) : limits.qualifying_amount,
      }
    case 'SMI':
      // Fall back to the Classic IUA if the membership somehow has no plan recorded.
      return {
        ministryName: 'Samaritan Ministries',
        qualifyingAmount: getSmiPlanLimits(ministry.plan ?? 'classic').iuaAmount,
      }
    default:
      return { ministryName: 'CHM', qualifyingAmount: limits.qualifying_amount }
  }
}
